import React, { useCallback, useMemo, useState } from 'react';
import { NoteContent } from 'components/HealthRecords/AppointmentNotes/AppointmentNoteDetails';
import Icon from 'components/Utils/NewIcon/index';
import ConfirmationDialog from 'components/Utils/ConfirmationDialog';
import './style.scss';
import CPPUtils from 'services/CPPUtils';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import PerformanceUtils from 'services/PerformanceUtils';
import { setFormValues } from 'hooks/useReduxForm';

export const ACTIVE_TAB = 'note';

interface CPPTimelineAppointmentNoteDraftProps {
  draft: any;
  patient: any;
  setActiveCPPTab: (tab: string) => void;
}

const CPPTimelineAppointmentNoteDraft = (props: CPPTimelineAppointmentNoteDraftProps) => {
  const [showDialog, setShowDialog] = useState(false);
  const dispatch = useAppDispatch();
  const authentication = useAppSelector((state) => state.authentication);
  const physicians = useAppSelector((state) => state.physicians.byId);

  const draft = props.draft;
  const author = physicians[draft.author_id];

  const ownId = authentication.user!.id;
  const canEdit = draft.author_id === ownId || author?.intern_of;

  const formName = CPPUtils.getFormName(ACTIVE_TAB, props.patient.id);

  async function confirmDelete() {
    setShowDialog(false);
    await dispatch(
      api.HealthRecords.deleteNotesDraft({
        patientId: draft.patient_id,
        id: draft.id,
      }),
      { errorTitle: 'Delete Draft' },
    );
  }

  const editCPPItem = useCallback(
    (event?: React.MouseEvent, additionalData: any = {}) => {
      let data = CPPUtils.getAppointmentNoteEditElement(draft.note, draft.id);
      data = { ...data, ...additionalData };
      dispatch(setFormValues({ formName, values: { ...data } }));
      props.setActiveCPPTab(ACTIVE_TAB);
    },
    [draft, formName, dispatch, props.setActiveCPPTab],
  );

  function openDialog() {
    setShowDialog(true);
  }

  function closeDialog() {
    setShowDialog(false);
  }

  const note = useMemo(() => {
    let note = PerformanceUtils.clone(draft.note);
    note.author_id = draft.author_id;

    if (!note.id) {
      note.value.value = '[UNSAVED DRAFT - NEW NOTE]\n ' + note.value.value;
    } else {
      note.value.value = '[UNSAVED DRAFT - EXISTING NOTE]\n ' + note.value.value;
    }
    return note;
  }, [draft]);

  let className = 'timeline-draft';

  return (
    <div className={className}>
      {!!showDialog && (
        <ConfirmationDialog
          open={showDialog}
          title="Delete Draft"
          message="This will delete the draft. Proceed?"
          confirmLabel="Confirm"
          onConfirm={confirmDelete}
          onCancel={closeDialog}
        />
      )}
      {canEdit && (
        <div className="hover-icons">
          <Icon
            src="trash"
            height="20px"
            width="20px"
            alt="Delete"
            showTooltip={true}
            className="trash"
            onClick={openDialog}
          />
          <Icon
            src="pencil"
            height="20px"
            width="20px"
            alt="Edit"
            showTooltip={true}
            className="icon-edit"
            onClick={() => editCPPItem()}
          />
        </div>
      )}
      <NoteContent physicians={physicians} note={note} date={draft.date} showInfoText={true} />
      {!!note.value.notes && <span>Notes: {note.value.notes}</span>}
    </div>
  );
};

export default CPPTimelineAppointmentNoteDraft;
